import { useState, useEffect, useRef, useCallback } from 'react';
import { fetchQueueStatus } from '_services/queue-status-service';

const POLL_INTERVAL_MS = 1500;

/**
 * Polls queue-status for a given requestId while active.
 *
 * @param {string|null} requestId - The request ID to track, or null to stop polling.
 * @returns {{ queueStatus: string|null, position: number|null, queueLength: number, inFlight: number }}
 */
const useQueueStatus = (requestId) => {
  const [status, setStatus] = useState(null);       // 'queued' | 'running' | 'unknown' | null
  const [position, setPosition] = useState(null);
  const [queueLength, setQueueLength] = useState(0);
  const [inFlight, setInFlight] = useState(0);
  const intervalRef = useRef(null);

  const poll = useCallback(async () => {
    if (!requestId) return;
    try {
      const result = await fetchQueueStatus(requestId);
      setStatus(result.status);
      setPosition(result.position);
      setQueueLength(result.queueLength);
      setInFlight(result.inFlight);
    } catch {
      // Silently ignore polling errors — transient network issues shouldn't crash UI
    }
  }, [requestId]);

  useEffect(() => {
    if (!requestId) {
      setStatus(null);
      setPosition(null);
      setQueueLength(0);
      setInFlight(0);
      return;
    }

    poll();
    intervalRef.current = setInterval(poll, POLL_INTERVAL_MS);

    return () => {
      if (intervalRef.current) clearInterval(intervalRef.current);
    };
  }, [requestId, poll]);

  return { queueStatus: status, position, queueLength, inFlight };
};

export default useQueueStatus;
